import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { SchemaField } from '@doneverse/data/utils'
import { getEffectiveDisplayType } from '@doneverse/data/utils'
import type { ShowWhen } from '@doneverse/shared'
import { Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react'
import type { Control } from 'react-hook-form'
import { useController } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

/** Maps display variant values back to canonical fieldType + displayAs */
const DISPLAY_VARIANT_MAP: Record<
  string,
  { fieldType: SchemaField['field_type']; displayAs?: SchemaField['displayAs'] }
> = {
  long_text_xl: { fieldType: 'long_text', displayAs: 'long_text_xl' },
  radio: { fieldType: 'single_select', displayAs: 'radio' },
  checkbox: { fieldType: 'single_select', displayAs: 'checkbox' },
  image_select: { fieldType: 'single_select', displayAs: 'image_select' },
  multi_list: { fieldType: 'short_text', displayAs: 'multi_list' },
}

export const FIELD_TYPES = [
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text' },
  { value: 'long_text_xl', label: 'Long Text (Large)' },
  { value: 'single_select', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-Select' },
  { value: 'rank', label: 'Rank / Ordering' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'image_select', label: 'Image Select' },
  { value: 'multi_list', label: 'Multi-Item List' },
]

function splitOptionLines(value: string) {
  return value.split(/\r?\n/)
}

function normalizeOptionLines(value: string) {
  return splitOptionLines(value)
    .map((option) => option.trim())
    .filter(Boolean)
}

export interface FieldEditorProps {
  control: Control<{ fields: SchemaField[] }>
  index: number
  fieldId: string | number
  fieldValues: SchemaField
  expanded: boolean
  onToggle: () => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  disableMoveUp: boolean
  disableMoveDown: boolean
  allFields: SchemaField[]
  styles: Record<string, string>
}

export function FieldEditor({
  control,
  index,
  fieldId,
  fieldValues,
  expanded,
  onToggle,
  onRemove,
  onMoveUp,
  onMoveDown,
  disableMoveUp,
  disableMoveDown,
  allFields,
  styles,
}: FieldEditorProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: fieldId,
  })

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  }

  const isSection = fieldValues.type === 'section'
  const fieldLabel = fieldValues.title || fieldValues.question || 'Untitled'
  const displayType = getEffectiveDisplayType(fieldValues)
  const hasOptions =
    displayType === 'single_select' ||
    displayType === 'multi_select' ||
    displayType === 'rank' ||
    displayType === 'radio' ||
    displayType === 'image_select'

  // Get previous question fields for conditional logic
  const previousFields = allFields.slice(0, index).filter((f) => !f.type)

  const displayName = isSection
    ? fieldValues.title || 'Untitled Section'
    : fieldValues.question || 'Untitled Question'
  const isPlaceholder = isSection ? !fieldValues.title : !fieldValues.question

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className={[
        styles.fieldCard,
        isSection ? styles.fieldCardSection : styles.fieldCardQuestion,
        isDragging ? styles.fieldCardDragging : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Header */}
      <div className={styles.fieldHeader} onClick={onToggle}>
        <button
          type="button"
          className={styles.fieldDragHandle}
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <GripVertical className={styles.fieldDragHandleIcon} />
        </button>
        <span
          className={`${styles.fieldTypeBadge} ${isSection ? styles.fieldTypeBadgeSection : styles.fieldTypeBadgeField}`}
        >
          {isSection ? 'Section' : displayType}
        </span>
        <span className={`${styles.fieldName} ${isPlaceholder ? styles.fieldNamePlaceholder : ''}`}>
          {displayName}
        </span>
        <div className={styles.fieldControls}>
          <button
            type="button"
            className={styles.controlBtn}
            onClick={(e) => {
              e.stopPropagation()
              onMoveUp()
            }}
            disabled={disableMoveUp}
            aria-label={`Move ${fieldLabel} up`}
          >
            <ChevronUp className="w-3 h-3" />
          </button>
          <button
            type="button"
            className={styles.controlBtn}
            onClick={(e) => {
              e.stopPropagation()
              onMoveDown()
            }}
            disabled={disableMoveDown}
            aria-label={`Move ${fieldLabel} down`}
          >
            <ChevronDown className="w-3 h-3" />
          </button>
          <button
            type="button"
            className={`${styles.controlBtn} ${styles.controlBtnDanger}`}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            aria-label={`Remove ${fieldLabel}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
          <ChevronDown className={`${styles.expandChevron} ${expanded ? styles.expandChevronOpen : ''}`} />
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className={styles.fieldBody}>
          <div className={styles.fieldBodyInner}>
            {isSection ? (
              <SectionFields control={control} index={index} styles={styles} />
            ) : (
              <QuestionFields
                control={control}
                index={index}
                fieldValues={fieldValues}
                hasOptions={hasOptions}
                previousFields={previousFields}
                styles={styles}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Section Fields ────────────────────────────────────── */

function SectionFields({
  control,
  index,
  styles,
}: {
  control: Control<{ fields: SchemaField[] }>
  index: number
  styles: Record<string, string>
}) {
  const titleCtrl = useController({ control, name: `fields.${index}.title` })
  const descCtrl = useController({ control, name: `fields.${index}.description` })

  return (
    <>
      <div className={styles.fieldGroup}>
        <Label className={styles.fieldLabel}>Section Title</Label>
        <Input
          ref={titleCtrl.field.ref}
          value={titleCtrl.field.value || ''}
          onChange={titleCtrl.field.onChange}
          onBlur={titleCtrl.field.onBlur}
          placeholder="e.g., About Your Business"
          autoFocus
        />
        {titleCtrl.fieldState.error?.message && (
          <p className={styles.fieldError}>{titleCtrl.fieldState.error.message}</p>
        )}
      </div>
      <div className={styles.fieldGroup}>
        <Label className={styles.fieldLabel}>Description (optional)</Label>
        <Textarea
          value={descCtrl.field.value || ''}
          onChange={descCtrl.field.onChange}
          onBlur={descCtrl.field.onBlur}
          placeholder="Additional context for this section..."
          className="min-h-[60px]"
        />
      </div>
    </>
  )
}

/* ─── Question Fields ───────────────────────────────────── */

function QuestionFields({
  control,
  index,
  fieldValues,
  hasOptions,
  previousFields,
  styles,
}: {
  control: Control<{ fields: SchemaField[] }>
  index: number
  fieldValues: SchemaField
  hasOptions: boolean
  previousFields: SchemaField[]
  styles: Record<string, string>
}) {
  const questionCtrl = useController({ control, name: `fields.${index}.question` })
  const descCtrl = useController({ control, name: `fields.${index}.description` })
  const fieldTypeCtrl = useController({ control, name: `fields.${index}.field_type` })
  const displayAsCtrl = useController({ control, name: `fields.${index}.displayAs` })
  const requiredCtrl = useController({ control, name: `fields.${index}.required` })
  const optionsCtrl = useController({ control, name: `fields.${index}.options` })
  const optionImagesCtrl = useController({ control, name: `fields.${index}.optionImages` })
  const showWhenCtrl = useController({ control, name: `fields.${index}.showWhen` })

  return (
    <>
      <div className={styles.fieldGroup}>
        <Label className={styles.fieldLabel}>Question</Label>
        <Input
          ref={questionCtrl.field.ref}
          value={questionCtrl.field.value || ''}
          onChange={questionCtrl.field.onChange}
          onBlur={questionCtrl.field.onBlur}
          placeholder="e.g., What is your target audience?"
          autoFocus
        />
        {questionCtrl.fieldState.error?.message && (
          <p className={styles.fieldError}>{questionCtrl.fieldState.error.message}</p>
        )}
      </div>
      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <Label className={styles.fieldLabel}>Field Type</Label>
          <Select
            value={getEffectiveDisplayType(fieldValues)}
            onValueChange={(v) => {
              const mapping = DISPLAY_VARIANT_MAP[v]
              if (mapping) {
                fieldTypeCtrl.field.onChange(mapping.fieldType)
                displayAsCtrl.field.onChange(mapping.displayAs)
              } else {
                fieldTypeCtrl.field.onChange(v)
                displayAsCtrl.field.onChange(undefined)
              }
            }}
          >
            <SelectTrigger className="bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background">
              {FIELD_TYPES.map((ft) => (
                <SelectItem key={ft.value} value={ft.value}>
                  {ft.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={requiredCtrl.field.value || false}
            onChange={(e) => requiredCtrl.field.onChange(e.target.checked)}
            className="rounded"
          />
          Required
        </label>
      </div>
      <div className={styles.fieldGroup}>
        <Label className={styles.fieldLabel}>Help Text (optional)</Label>
        <Input
          value={descCtrl.field.value || ''}
          onChange={descCtrl.field.onChange}
          onBlur={descCtrl.field.onBlur}
          placeholder="Additional guidance for this field..."
        />
      </div>

      {/* Options for select fields */}
      {hasOptions && (
        <div className={styles.fieldGroup}>
          <Label className={styles.fieldLabel}>Options (one per line)</Label>
          <Textarea
            ref={optionsCtrl.field.ref}
            value={(optionsCtrl.field.value || []).join('\n')}
            onChange={(e) => optionsCtrl.field.onChange(splitOptionLines(e.target.value))}
            onBlur={(e) => {
              optionsCtrl.field.onChange(normalizeOptionLines(e.target.value))
              optionsCtrl.field.onBlur()
            }}
            placeholder="Option 1&#10;Option 2&#10;Option 3"
            className="min-h-[80px]"
          />
          {optionsCtrl.fieldState.error?.message && (
            <p className={styles.fieldError}>{optionsCtrl.fieldState.error.message}</p>
          )}
        </div>
      )}

      {getEffectiveDisplayType(fieldValues) === 'image_select' &&
        (optionsCtrl.field.value || []).filter(Boolean).length > 0 && (
          <div className={styles.fieldGroup}>
            <Label className={styles.fieldLabel}>Image URLs (one per option)</Label>
            {(optionsCtrl.field.value as string[]).filter(Boolean).map((option) => {
              const inputId = `img-url-${index}-${option}`
              return (
                <div key={option} className={styles.optionImageRow}>
                  <Label htmlFor={inputId} className={styles.optionImageLabel}>
                    {option}
                  </Label>
                  <Input
                    id={inputId}
                    value={
                      (optionImagesCtrl.field.value as Record<string, string> | undefined)?.[option] || ''
                    }
                    onChange={(e) => {
                      const current =
                        (optionImagesCtrl.field.value as Record<string, string> | undefined) || {}
                      const updated = { ...current }
                      if (e.target.value) {
                        updated[option] = e.target.value
                      } else {
                        delete updated[option]
                      }
                      optionImagesCtrl.field.onChange(Object.keys(updated).length > 0 ? updated : undefined)
                    }}
                    placeholder="https://..."
                    className="text-xs"
                  />
                </div>
              )
            })}
          </div>
        )}

      {/* Conditional Logic — multi-value equals */}
      {previousFields.length > 0 && (
        <div className={styles.conditionalBox}>
          <Label className={styles.fieldLabel}>Conditional Display (optional)</Label>
          <div className={styles.conditionalRow}>
            <Select
              value={fieldValues.showWhen?.questionId || 'none'}
              onValueChange={(v) => {
                if (v === 'none') {
                  showWhenCtrl.field.onChange(undefined)
                } else {
                  showWhenCtrl.field.onChange({
                    questionId: v,
                    equals: fieldValues.showWhen?.equals || [],
                  })
                }
              }}
            >
              <SelectTrigger className="bg-background text-xs">
                <SelectValue placeholder="Show when..." />
              </SelectTrigger>
              <SelectContent className="bg-background">
                <SelectItem value="none">Always show</SelectItem>
                {previousFields.map((pf, i) => (
                  <SelectItem key={pf.id} value={pf.id.toString()}>
                    Q{i + 1}: {(pf.question || '').slice(0, 30)}...
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldValues.showWhen && (
              <ShowWhenEquals
                showWhen={fieldValues.showWhen}
                previousFields={previousFields}
                onChange={(equals) =>
                  showWhenCtrl.field.onChange({
                    ...fieldValues.showWhen!,
                    equals,
                  })
                }
                styles={styles}
              />
            )}
          </div>
        </div>
      )}
    </>
  )
}

/* ─── ShowWhen Equals (multi-value) ────────────────────── */

function normalizeEquals(equals: string | string[] | undefined): string[] {
  if (!equals) return []
  if (Array.isArray(equals)) return equals
  return [equals]
}

function ShowWhenEquals({
  showWhen,
  previousFields,
  onChange,
  styles,
}: {
  showWhen: ShowWhen
  previousFields: SchemaField[]
  onChange: (equals: string[]) => void
  styles: Record<string, string>
}) {
  const refField = previousFields.find((f) => f.id.toString() === showWhen.questionId)
  const options = refField?.options
  const selected = normalizeEquals(showWhen.equals)

  // Referenced question has options → show checkboxes
  if (options && options.length > 0) {
    return (
      <div className={styles.equalsCheckboxList}>
        {options.map((opt) => (
          <label key={opt} className={styles.equalsCheckboxItem}>
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={(e) => {
                const next = e.target.checked ? [...selected, opt] : selected.filter((v) => v !== opt)
                onChange(next)
              }}
              className="rounded"
            />
            <span className={styles.equalsCheckboxLabel}>{opt}</span>
          </label>
        ))}
      </div>
    )
  }

  // Fallback for free-text questions: plain text input (single value)
  return (
    <Input
      value={selected[0] || ''}
      onChange={(e) => {
        const v = e.target.value
        onChange(v ? [v] : [])
      }}
      placeholder="equals this value..."
      className="text-xs"
    />
  )
}
